import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useGetList } from "../../../../hooks/useGetList";
import { useParams } from "react-router-dom";
import { usePost } from "../../../../hooks/usePost";
import { useUpdate } from "../../../../hooks/useUpdate";
import { useDispatch, useSelector } from "react-redux";
import { clearEditUnitRelation } from "../../../../redux/unitRelation/unitRelationSlice";
import { setRefresh } from "../../../../redux/refreshSlice";

const UnitRelationForm = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      relatedFromUnit: "",
      relatedToUnit: "",
      fromUnitQty: "",
      toUnitQty: "",
      createInverted: false,
    },
  });

  const { id } = useParams();
  const { selectedBranch } = useSelector((state) => state.filterItemUnit);
  const { data, loading, refetch } = useGetList(
    `item-unit/options?relatedItem=${id}`,
  );
  const { postData, loading: postLoading } = usePost(`unit-relation`);

  const { editUnitRelation } = useSelector((state) => state.unitRelation);
  const { refresh } = useSelector((state) => state.refresh);

  const { updateData, loading: updateLoading } = useUpdate(`unit-relation`);
  const dispatch = useDispatch();

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      relatedItem: id,
    };
    if (editUnitRelation?.isEditMode) {
      await updateData(editUnitRelation?.data?._id, payload);
      dispatch(setRefresh(!refresh));
    } else {
      const postPayload = {
        ...payload,
        relatedBranch: selectedBranch,
      };
      await postData(postPayload);
      dispatch(setRefresh(!refresh));
    }
  };

  useEffect(() => {
    if (!editUnitRelation.isEditMode)
      reset({
        relatedFromUnit: "",
        relatedToUnit: "",
        fromUnitQty: "",
        toUnitQty: "",
      });

    if (editUnitRelation.isEditMode && editUnitRelation.data) {
      reset({
        relatedFromUnit: editUnitRelation?.data?.relatedFromUnit._id,
        relatedToUnit: editUnitRelation?.data?.relatedToUnit._id,
        fromUnitQty: editUnitRelation?.data?.fromUnitQty.toString(),
        toUnitQty: editUnitRelation?.data?.toUnitQty.toString(),
      });
    } else {
      reset({
        relatedFromUnit: "",
        relatedToUnit: "",
        fromUnitQty: "",
        toUnitQty: "",
      });
    }
  }, [editUnitRelation]);

  useEffect(() => {
    refetch();
  }, [refresh]);
  return (
    <>
      <Paper elevation={3} sx={{ p: 3, mt: 5 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          {editUnitRelation?.isEditMode ? "Edit" : "Create"} Unit Relation
        </Typography>

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <Controller
                name="relatedFromUnit"
                control={control}
                rules={{
                  required: editUnitRelation?.isEditMode
                    ? false
                    : "From unit is required",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="From Unit"
                    variant="outlined"
                    fullWidth
                    size="small"
                    disabled={postLoading || editUnitRelation.isEditMode}
                    error={!!errors.relatedFromUnit}
                    helperText={errors.relatedFromUnit?.message}
                  >
                    {!loading &&
                      data?.data?.map((option) => (
                        <MenuItem key={option._id} value={option._id}>
                          {option.name}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <Controller
                name="fromUnitQty"
                control={control}
                rules={{
                  required: "From Quantity is required",
                  pattern: {
                    value: /^\d*\.?\d+$/,
                    message: "Please enter a valid number",
                  },
                  min: {
                    value: 1,
                    message: "From Quantity must be greater than 0",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="From Quantity"
                    variant="outlined"
                    fullWidth
                    type="number"
                    size="small"
                    disabled={postLoading || updateLoading}
                    inputProps={{ step: "1", min: "1" }}
                    error={!!errors.fromUnitQty}
                    helperText={errors.fromUnitQty?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <Controller
                name="relatedToUnit"
                control={control}
                rules={{
                  required: editUnitRelation?.isEditMode
                    ? false
                    : "To unit is required",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="To Unit"
                    variant="outlined"
                    fullWidth
                    size="small"
                    disabled={postLoading || editUnitRelation.isEditMode}
                    error={!!errors.relatedToUnit}
                    helperText={errors.relatedToUnit?.message}
                  >
                    {!loading &&
                      data?.data?.map((option) => (
                        <MenuItem key={option._id} value={option._id}>
                          {option.name}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <Controller
                name="toUnitQty"
                control={control}
                rules={{
                  required: "To Quantity is required",
                  pattern: {
                    value: /^\d*\.?\d+$/,
                    message: "Please enter a valid number",
                  },
                  min: {
                    value: 1,
                    message: "To Quantity must be greater than 0",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="To Unit Quantity"
                    variant="outlined"
                    fullWidth
                    type="number"
                    size="small"
                    disabled={postLoading || updateLoading}
                    inputProps={{ step: "1", min: "1" }}
                    error={!!errors.toUnitQty}
                    helperText={errors.toUnitQty?.message}
                  />
                )}
              />
            </Grid>

            {!editUnitRelation?.isEditMode && (
              <Grid item xs={12} sm={2}>
                <Controller
                  name="createInverted"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value || false}
                          color="primary"
                          size="small"
                          disabled={postLoading}
                        />
                      }
                      label="Reverse"
                    />
                  )}
                />
              </Grid>
            )}
            {/* <Grid item xs={12} sm={2}>
              <Controller
                name="createInverted"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value || false}
                        color="primary"
                        size="small"
                        disabled={postLoading}
                      />
                    }
                    label="Reverse"
                  />
                )}
              />
            </Grid> */}

            <Grid item xs={12} sm={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="small"
                fullWidth
                disabled={postLoading}
              >
                {updateLoading
                  ? "Updating..."
                  : loading
                    ? "Submitting..."
                    : editUnitRelation.isEditMode
                      ? "Update"
                      : "Submit"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </>
  );
};

export default UnitRelationForm;
